import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import {
	AlertTriangle,
	Box,
	Building2,
	ChevronDown,
	ChevronUp,
	Cloud,
	Database,
	EthernetPort,
	Layers,
	MapPin,
	Network,
	Pin,
	Route,
	Server,
	TableProperties,
	Shield,
	Waypoints,
	Zap,
} from "lucide-react";
import type { InsightChipRef, InsightConnectivityRef, InsightRouteEntry } from "../../types/insightApi";

/**
 * Semantic ReactFlow node renderers for `InsightTopologyViewV2` (CTX-113
 * "ordered VPC cards with NACL/SG chips").
 *
 * `TopologyView.tsx` (the legacy static-artifact view) defines the
 * established Scarif visual language inline — green VPC/VCN boundaries,
 * dashed AZ containers, blue subnet containers with route-table/NACL/SG
 * badges, and semantic resource cards for instances/ENIs/route
 * tables/gateways/security groups — but does not export any of it. These
 * renderers deliberately mirror that established language (same
 * borders/colors/badge placement/dark-mode tokens) so the two views read as
 * one product, adapted to the V2 digest's flat `data: Record<string,
 * unknown>` node shape instead of the legacy newline-joined
 * `"VPC: name\nid\ncidr"` label string.
 *
 * CTX-110 used to derive subnet<->route-table / eni<->security-group /
 * instance<->eni relationships in the browser by classifying the digest's
 * plain edge list (`topologyRelationships.ts`). CTX-113's versioned
 * presentation contract removes the need for that entirely: a subnet's
 * effective NACL and an ENI's deduplicated security groups (and resolved
 * owning instance) are attached directly to the node's own `data` by
 * insight-data-service (`effectiveNacl` / `securityGroups` /
 * `resolvedInstance`) — these renderers read those fields as-is and never
 * reconstruct an equivalent mapping from `edges`. Route tables and
 * instances no longer show a derived summary of "what points at me" either
 * — those associations are communicated by containment/ordering (route
 * tables shelved at the top of their VPC) and, for the ENI's owning
 * instance, a direct decoration.
 */

export interface InsightNodeData {
	label?: string;
	kind?: string;
	/** Only set for containment ("group") nodes — providerPartition | region | availabilityGroup. */
	groupKind?: string;
	ctxrUuids?: string[];
	/** Cloud-native resource id (e.g. `vpc-0123...`), distinct from the ReactFlow node id. */
	nativeId?: string;
	cidr?: string;
	/** CTX-113 subnet decoration — the subnet's single resolved effective NACL/security list, read as-is. */
	effectiveNacl?: InsightChipRef | null;
	/** CTX-128 subnet decoration — the subnet's single resolved effective route table, rendered as a chip centered at the top of the card. */
	effectiveRouteTable?: InsightChipRef | null;
	/** CTX-113 ENI decoration — deduplicated security-group membership, read as-is. */
	securityGroups?: InsightChipRef[];
	/** CTX-128 — set when insight-data-service could not resolve this ENI's Security Groups (e.g. a missing permission) so the card can say so instead of silently showing zero chips. */
	securityGroupsUnavailable?: boolean;
	/** CTX-113 ENI decoration — the owning instance the backend was able to resolve, if any. */
	resolvedInstance?: InsightChipRef | null;
	/** CTX-128 ENI decoration — coarse health signal rolled into the collapsed subnet summary's warning/error counts. */
	status?: "ok" | "warning" | "error";
	/** CTX-128 route-table decoration — total individual-route count, shown as one number rather than one line per route. */
	routeCount?: number;
	/** CTX-128 route-table decoration — proven connectivity targets (IGW/NAT/TGW/VGW/peering/endpoint/Direct Connect), at most one per unique route-table/target pair. Rendered as family icon chips on the card's border. */
	connectivity?: InsightConnectivityRef[];
	/** CTX-128 route-table decoration — full individual-route list, rendered only in the route table's own detail panel, never on the overview card. */
	routes?: InsightRouteEntry[];
	/** Set by `toReactFlowNodes` when this node's `parentId` didn't resolve within the same response — an explicit "ungrouped" treatment instead of silently nesting it at the root. */
	orphaned?: boolean;
	/** Lowercased, trimmed active search query — threaded through so chips can highlight their own match independently of whole-card dimming. */
	searchQuery?: string;
	onSelectRelated?: (id: string | null) => void;
	/** CTX-167 "semantic zoom" — true below a useful zoom threshold; a card should show only its boundary/name/counts/warnings and skip per-item fine detail (IP addresses, chip lists) that would be illegible anyway. Undefined/false everywhere the canvas hasn't wired real viewport tracking (e.g. every existing test fixture) preserves full detail. */
	lowDetail?: boolean;
	/** CTX-128 — set by `eniDisclosure` on the synthetic "+N ENIs" overflow card; counts describe the ENIs collapsed behind it. */
	hiddenTotal?: number;
	hiddenAttached?: number;
	hiddenUnattached?: number;
	hiddenWarning?: number;
	hiddenError?: number;
	/** CTX-128 — whether the owning subnet is currently expanded (all ENIs visible) vs collapsed (bounded + overflow card). */
	expanded?: boolean;
	onToggleExpand?: () => void;
	/** CTX-167 — set by `hierarchyDisclosure` on a collapsed VPC/AZ's compact summary card; rolled-up counts of the descendants that were removed from the visible set. */
	summaryCounts?: {
		subnetCount: number;
		eniCount: number;
		routeTableCount: number;
		warningCount: number;
		errorCount: number;
		/** VPC-level connection resources drawn as icon badges on the card's top border — true attachments (IGW, TGW attachment, peering, DX) plus NAT (subnet-resident, but a VPC-level egress function), which the route tables then reference. */
		attachments?: { igw: number; nat: number; tgw: number; peering: number; directConnect: number };
	};
	/** CTX-167 — the original VPC/AZ id a summary/overflow card stands in for, or the scope kind an overflow card collapses (`"vpc" | "az" | "subnet" | "regional" | "shelf"`). `hiddenTotal` (declared above for the CTX-128 ENI overflow card) is reused as-is for the scope-overflow card's own "+N" count. */
	scopeId?: string;
	scopeLevel?: "vpc" | "az" | "subnet" | "regional" | "shelf";
	/** Set on the synthetic attachment nodes drawn above a collapsed VPC card — which connection family (`igw`/`nat`/`tgw`/`peering`/`directConnect`) this node represents, and how many of them the VPC has. */
	attachmentFamily?: string;
	attachmentCount?: number;
	/** Every member gateway of the family — real node id + AWS name label + native id, for the node's tooltip and the sidebar list. */
	attachmentGateways?: Array<{ id: string; label: string; nativeId?: string }>;
	/** CTX-167 — clicking a compact VPC/AZ summary card focuses that scope (drills in one level). */
	onFocus?: () => void;
	/** CTX-167 — clicking a "+N" scope-overflow card bypasses that level's visible-count cap. */
	onExpandScope?: () => void;
	/** CTX-167 — whether the focused VPC/AZ is pinned (stays expanded even once a sibling is focused). Shown as a small pin toggle on the focused card's header. */
	pinned?: boolean;
	/** Whether this expanded VPC is the CURRENTLY FOCUSED one (breadcrumb target) — gets the strong border/glow treatment; other open (pinned) VPCs render quieter so the active scope is unmistakable. */
	focused?: boolean;
	onTogglePin?: () => void;
	/** Collapses this expanded VPC back to its compact summary card — the explicit close control (opening another VPC no longer closes this one implicitly). */
	onCollapse?: () => void;
	[key: string]: unknown;
}

type InsightNodeProps = NodeProps<InsightNodeData>;

// The per-node "contributed by N CTXRs" count badge was removed (2026-08-06):
// on the common case — an HA cluster whose two members both discover the same
// account — it rendered an identical "2" on every single card, which is noise
// on every card rather than signal on any. `data.ctxrUuids` is still carried on
// the node (see `InsightNodeData`) for detail-panel provenance and for the
// per-member scoping work; it is simply not decorated onto the card.

/** Explicit "unassociated" treatment (CTX-113) for a node whose containing group couldn't be resolved — never silently nested at the root without a visual signal. */
function OrphanBadge({ orphaned }: { orphaned?: boolean }) {
	if (!orphaned) return null;
	return (
		<span
			data-testid="node-orphan-badge"
			title="This resource's containing group could not be resolved."
			className="absolute -top-2 -left-2 z-20 rounded-full border border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-amber-700 dark:border-semantic-border-warning-primary dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning"
		>
			Unassociated
		</span>
	);
}

function orphanRingClass(data: InsightNodeData): string {
	return data.orphaned ? "ring-2 ring-amber-400" : "";
}

function nativeIdOf(data: InsightNodeData, fallbackId: string): string {
	return typeof data.nativeId === "string" && data.nativeId.length > 0 ? data.nativeId : fallbackId;
}

/** Provider/account boundary — Cloud/Kontrol's top-level partition (AWS account, OCI tenancy...). */
export const InsightProviderNode = memo(({ data }: InsightNodeProps) => (
	<div
		className={`relative h-full w-full rounded-3xl border-2 border-dashed border-slate-300/70 bg-transparent dark:border-semantic-border-primary ${orphanRingClass(data)}`}
	>
		<Handle type="target" position={Position.Left} className="opacity-0" />
		<Handle type="source" position={Position.Right} className="opacity-0" />
		<div className="absolute -top-3 left-6 flex items-center gap-1.5 rounded-full border border-transparent bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:bg-semantic-bg-secondary dark:text-semantic-text-secondary dark:border-semantic-border-primary">
			<Building2 className="h-3 w-3 shrink-0" />
			<span className="truncate">{data.label}</span>
		</div>
		<OrphanBadge orphaned={data.orphaned} />
	</div>
));
InsightProviderNode.displayName = "InsightProviderNode";

/** Region boundary — same minimalist treatment as the account boundary, one level down. */
export const InsightRegionNode = memo(({ data }: InsightNodeProps) => (
	<div
		className={`relative h-full w-full rounded-3xl border-2 border-dashed border-slate-300/60 bg-transparent dark:border-semantic-border-primary dark:bg-semantic-bg-secondary/30 ${orphanRingClass(data)}`}
	>
		<Handle type="target" position={Position.Left} className="opacity-0" />
		<Handle type="source" position={Position.Right} className="opacity-0" />
		<div className="absolute -top-3 left-6 flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:bg-semantic-bg-secondary dark:text-semantic-text-secondary">
			<Cloud className="h-3 w-3 shrink-0" />
			<span className="truncate">{data.label}</span>
		</div>
		<OrphanBadge orphaned={data.orphaned} />
	</div>
));
InsightRegionNode.displayName = "InsightRegionNode";

/** Availability zone / availability domain container — dotted indigo border, floating label top-right. */
export const InsightAzNode = memo(({ data }: InsightNodeProps) => (
	<div
		className={`relative h-full w-full rounded-xl border-2 border-dotted border-indigo-300 bg-transparent dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary ${orphanRingClass(data)}`}
	>
		<Handle type="target" position={Position.Left} className="opacity-0" />
		<Handle type="source" position={Position.Right} className="opacity-0" />
		<div className="absolute -top-3 right-6 flex items-center gap-1 rounded border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-indigo-500 dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary dark:text-semantic-text-secondary">
			<MapPin className="h-2.5 w-2.5 shrink-0" />
			<span className="truncate">{data.label}</span>
			<PinToggle pinned={data.pinned} onTogglePin={data.onTogglePin} />
			{/*
				An expanded AZ needs its own collapse control, exactly like the VPC
				header's. Without it the only ways back were the breadcrumb and the
				Escape key, so an AZ opened by clicking its card looked permanently
				stuck open (2026-08-06).
			*/}
			{data.onCollapse && (
				<button
					type="button"
					data-testid="az-collapse-toggle"
					title="Collapse this availability zone back to its compact card"
					onClick={(event) => {
						event.stopPropagation();
						data.onCollapse?.();
					}}
					className="rounded-full p-0.5 text-indigo-400 hover:text-indigo-600 dark:text-semantic-text-tertiary dark:hover:text-semantic-text-primary"
				>
					<ChevronUp className="h-3 w-3" />
				</button>
			)}
		</div>
		<OrphanBadge orphaned={data.orphaned} />
	</div>
));
InsightAzNode.displayName = "InsightAzNode";

/** VPC / VCN boundary — established solid green border + floating "VPC" header with name/id/CIDR. */
export const InsightVpcNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const isVcn = data.kind === "vcn";
	// The focused VPC (breadcrumb target) is unmistakable: heavier border plus
	// a soft outer glow; pinned-but-not-focused VPCs stay on a quieter border
	// so multiple open VPCs still read at a glance.
	const borderTreatment = data.focused
		? "border-[3px] border-green-600 shadow-[0_0_0_5px_rgba(22,163,74,0.16)]"
		: "border-2 border-green-400/80 shadow-sm";
	return (
		<div
			data-testid="insight-vpc-node"
			data-focused={data.focused ? "true" : undefined}
			className={`relative h-full w-full rounded-2xl bg-green-50/30 dark:bg-semantic-bg-secondary dark:border-semantic-border-success-primary ${borderTreatment} ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<div
				className={`absolute -top-4 left-6 z-10 flex items-center gap-2 rounded-lg px-4 py-1.5 shadow-md dark:bg-semantic-bg-success-bold ${data.focused ? "bg-green-600" : "bg-green-500/90"}`}
			>
				<Cloud className="h-3.5 w-3.5 text-white dark:text-semantic-text-inverse-primary" />
				<span className="text-[11px] font-bold tracking-wide text-white dark:text-semantic-text-inverse-primary">
					{isVcn ? "VCN" : "VPC"}
				</span>
				<PinToggle pinned={data.pinned} onTogglePin={data.onTogglePin} />
				{data.onCollapse && (
					<button
						type="button"
						data-testid="vpc-collapse-toggle"
						title="Collapse this VPC back to its compact card"
						onClick={(event) => {
							event.stopPropagation();
							data.onCollapse?.();
						}}
						className="rounded-full p-0.5 text-white/60 hover:text-white"
					>
						<ChevronUp className="h-3 w-3" />
					</button>
				)}
			</div>
			<div className="flex flex-col gap-0.5 pl-6 pt-6">
				<div className="text-sm font-bold text-green-900/90 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				<div className="font-mono text-[10px] text-green-700/70 dark:text-semantic-text-secondary">{nativeId}</div>
				{data.cidr ? (
					<div className="font-mono text-[10px] font-medium text-green-600/80 dark:text-semantic-text-secondary">
						{String(data.cidr)}
					</div>
				) : null}
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightVpcNode.displayName = "InsightVpcNode";

function chipMatches(item: InsightChipRef, query?: string): boolean {
	if (!query) return false;
	const needle = query.trim().toLowerCase();
	if (!needle) return false;
	return (
		item.label.toLowerCase().includes(needle) ||
		item.id.toLowerCase().includes(needle) ||
		(item.nativeId ?? "").toLowerCase().includes(needle)
	);
}

/**
 * Compact chip row with a bounded `+N` overflow treatment (CTX-113: "large
 * chip sets collapse to a bounded +N rather than expanding cards without
 * limit") — the full remainder is still available via the `+N` chip's
 * tooltip, never via an expand-in-place toggle that would grow the card.
 * Reused for a subnet's single effective-NACL chip and an ENI's
 * deduplicated security-group chips.
 */
const CHIP_TONE_CLASS: Record<"blue" | "emerald" | "sky", string> = {
	blue: "border-blue-100 bg-blue-50 text-blue-700 dark:border-semantic-border-brand-primary dark:bg-semantic-bg-brand-secondary dark:text-semantic-text-brand",
	emerald:
		"border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-semantic-border-success-primary dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success",
	sky: "border-sky-200 bg-sky-50 text-sky-700 dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary dark:text-semantic-text-secondary",
};

function ChipList({
	items,
	testId,
	max = 4,
	onSelectRelated,
	searchQuery,
	tone = "blue",
}: {
	items: InsightChipRef[];
	testId: string;
	max?: number;
	onSelectRelated?: (id: string | null) => void;
	searchQuery?: string;
	tone?: "blue" | "emerald" | "sky";
}) {
	const visible = items.slice(0, max);
	const overflow = items.slice(max);
	return (
		<div className="flex flex-wrap justify-center gap-1 px-1.5" data-testid={testId}>
			{visible.map((item) => {
				const matched = chipMatches(item, searchQuery);
				return (
					<button
						key={item.id}
						type="button"
						data-testid={`${testId}-chip`}
						data-chip-match={matched ? "true" : undefined}
						onClick={(event) => {
							event.stopPropagation();
							onSelectRelated?.(item.id);
						}}
						title={item.nativeId && item.nativeId !== item.label ? `${item.label} (${item.nativeId})` : item.label}
						className={`truncate rounded-full border px-1.5 py-0.5 text-center text-[8px] font-medium ${
							matched
								? "border-amber-400 bg-amber-100 text-amber-800 ring-1 ring-amber-400 dark:border-semantic-border-warning-primary dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning"
								: CHIP_TONE_CLASS[tone]
						}`}
					>
						{item.label}
					</button>
				);
			})}
			{overflow.length > 0 && (
				<span
					data-testid={`${testId}-overflow`}
					title={overflow.map((item) => item.label).join(", ")}
					className="cursor-default truncate rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-center text-[8px] font-semibold text-indigo-600 dark:border-semantic-border-brand-primary dark:bg-semantic-bg-brand-secondary dark:text-semantic-text-brand"
				>
					+{overflow.length}
				</span>
			)}
		</div>
	);
}

/**
 * Subnet boundary — established dashed blue border, SUBNET corner label,
 * the processor-resolved effective route table centered at the top of the
 * card (CTX-128 — the only per-subnet route association shown; individual
 * routes live in the route table's own detail, never a derived line here),
 * and (CTX-113) the subnet's single effective-NACL chip read directly from
 * `data.effectiveNacl`.
 */
export const InsightSubnetNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const effectiveNacl = (data.effectiveNacl ?? null) as InsightChipRef | null;
	const effectiveRouteTable = (data.effectiveRouteTable ?? null) as InsightChipRef | null;

	return (
		<div
			data-testid="insight-subnet-node"
			className={`relative h-full w-full rounded-lg border-2 border-dashed border-blue-500 bg-blue-50/50 dark:border-semantic-border-brand-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />

			{effectiveRouteTable && (
				<div className="absolute -top-4 left-1/2 z-20 -translate-x-1/2 transform">
					<ChipList
						items={[effectiveRouteTable]}
						testId="subnet-route-table-chip"
						max={1}
						tone="emerald"
						onSelectRelated={data.onSelectRelated}
						searchQuery={data.searchQuery}
					/>
				</div>
			)}

			{/*
				The NACL chip sits INSIDE the card's bottom padding
				(`SUBNET_PADDING_BOTTOM`), not straddling the border. Hanging it
				outside (`-bottom-4`) made it collide with the route-table chip of the
				subnet in the row below, whose own chip hangs above ITS border — the
				ELK row gap is smaller than the two overflows combined (2026-08-06).
				Keeping both decorations within their own card is the fix that needs
				no layout-profile bump and therefore no re-digest.
			*/}
			{effectiveNacl && (
				<div className="absolute bottom-0.5 left-1/2 z-20 -translate-x-1/2 transform">
					<ChipList
						items={[effectiveNacl]}
						testId="subnet-nacl-chip"
						max={1}
						onSelectRelated={data.onSelectRelated}
						searchQuery={data.searchQuery}
					/>
				</div>
			)}

			<div className="absolute left-0 top-0 rounded-br-lg border-b border-r border-blue-200/50 bg-blue-100 px-2 py-1 text-[9px] font-bold tracking-tight text-blue-700 dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary dark:text-semantic-text-primary">
				SUBNET
			</div>

			<div className="pointer-events-none absolute inset-0 flex flex-col items-start justify-start gap-1 px-3 pt-8">
				<div
					className="pointer-events-auto w-full select-text truncate font-mono text-[11px] font-bold leading-tight text-blue-900 dark:text-semantic-text-primary"
					title={label}
				>
					{label}
				</div>
				<div className="pointer-events-auto select-text truncate font-mono text-[9px] font-medium leading-none text-blue-800 dark:text-semantic-text-secondary">
					{nativeId}
				</div>
				{data.cidr ? (
					<div className="pointer-events-auto select-text truncate font-mono text-[9px] font-medium leading-none text-blue-700 dark:text-semantic-text-secondary">
						{String(data.cidr)}
					</div>
				) : null}
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightSubnetNode.displayName = "InsightSubnetNode";

/** Instance card — established white card, orange Server icon. Lives inside its subnet alongside its ENI(s) (CTX-113: containment/ordering, not a derived instance<->eni edge or summary). */
export const InsightInstanceNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);

	return (
		<div
			data-testid="insight-instance-node"
			className={`group relative flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-semantic-border-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<Server className="h-7 w-7 text-orange-500" strokeWidth={1.5} />
			<div className="w-full px-1 text-center">
				<div className="truncate text-[9px] font-semibold leading-tight text-slate-800 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				<div className="mt-0.5 truncate font-mono text-[8px] text-slate-400 dark:text-semantic-text-secondary">{nativeId}</div>
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightInstanceNode.displayName = "InsightInstanceNode";

const ENI_STATUS_DOT_CLASS: Record<"warning" | "error", string> = {
	warning: "bg-amber-400",
	error: "bg-rose-500",
};

/** Small filled dot for a non-"ok" ENI `status`, rolled up into the collapsed subnet summary's own warning/error counts (CTX-128). Absent (not just "ok"-colored) when status is "ok"/unset — never a derived state we invented. */
function EniStatusDot({ status }: { status?: "ok" | "warning" | "error" }) {
	if (status !== "warning" && status !== "error") return null;
	return (
		<span
			data-testid="eni-status-dot"
			data-status={status}
			title={status === "error" ? "Error" : "Warning"}
			className={`absolute -right-1 -top-1 z-10 h-2.5 w-2.5 rounded-full border border-white dark:border-semantic-bg-secondary ${ENI_STATUS_DOT_CLASS[status]}`}
		/>
	);
}

/** Standalone/attached ENI or VNIC card — orange EthernetPort icon, IP metadata, resolved-instance decoration, and (CTX-113) deduplicated security-group chips read directly from `data.securityGroups`. */
export const InsightEniNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const securityGroups = Array.isArray(data.securityGroups) ? (data.securityGroups as InsightChipRef[]) : [];
	const securityGroupsUnavailable = Boolean(data.securityGroupsUnavailable);
	const resolvedInstance = (data.resolvedInstance ?? null) as InsightChipRef | null;
	const privateIp = typeof data.privateIp === "string" ? data.privateIp : undefined;
	const publicIp = typeof data.publicIp === "string" ? data.publicIp : undefined;

	return (
		<div
			data-testid="insight-eni-node"
			className={`relative flex h-full w-full flex-col gap-0.5 rounded-lg border border-slate-200 bg-white py-1.5 pl-1 pr-2 shadow-sm dark:border-semantic-border-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<div className="absolute -left-2 -top-2 z-10 rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-semantic-border-primary dark:bg-semantic-bg-secondary">
				<EthernetPort className="h-4 w-4 text-orange-500 dark:text-semantic-text-warning" strokeWidth={1.5} />
			</div>
			<EniStatusDot status={data.status} />
			<div className="flex min-w-0 flex-col gap-0.5 pl-5">
				<div className="truncate text-[11px] font-semibold text-slate-800 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				{/* The native id duplicates the label on nearly every real ENI — only shown when it actually differs. "Standalone" is the unmarked default (the detail panel still states it); only an attached instance earns a line. */}
				{nativeId !== label && (
					<div className="truncate font-mono text-[9px] text-slate-500 dark:text-semantic-text-secondary">{nativeId}</div>
				)}
				{!data.lowDetail && privateIp && (
					<div className="truncate font-mono text-[9px] text-slate-600 dark:text-semantic-text-secondary">private: {privateIp}</div>
				)}
				{!data.lowDetail && publicIp && (
					<div className="truncate font-mono text-[9px] text-slate-600 dark:text-semantic-text-secondary">public: {publicIp}</div>
				)}
				{!data.lowDetail && resolvedInstance && (
					<div
						className="truncate font-mono text-[9px] text-slate-500 dark:text-semantic-text-secondary"
						title={resolvedInstance.label}
					>
						inst: {resolvedInstance.label}
					</div>
				)}
			</div>
			{!data.lowDetail && securityGroupsUnavailable && (
				<div
					data-testid="eni-security-groups-unavailable"
					title="Security Group data is unavailable for this resource — the processing role may be missing permission to enumerate Security Groups."
					className="mt-1 truncate border-t border-slate-200 pt-1 text-center text-[8px] font-medium uppercase tracking-wide text-amber-600 dark:border-semantic-border-primary dark:text-semantic-text-warning"
				>
					Security Groups unavailable
				</div>
			)}
			{!data.lowDetail && !securityGroupsUnavailable && securityGroups.length > 0 && (
				<div className="mt-1 border-t border-slate-200 pt-1 dark:border-semantic-border-primary">
					<ChipList
						items={securityGroups}
						testId="eni-security-group-chips"
						onSelectRelated={data.onSelectRelated}
						searchQuery={data.searchQuery}
					/>
				</div>
			)}
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightEniNode.displayName = "InsightEniNode";

/** Connectivity-target kind -> the attachment family whose chip identity it shares on a route-table card's border. */
const CONNECTIVITY_KIND_FAMILY: Record<InsightConnectivityRef["kind"], keyof typeof ATTACHMENT_FAMILY_META> = {
	igw: "igw",
	"egress-only-igw": "igw",
	nat: "nat",
	tgw: "tgw",
	vgw: "vgw",
	peering: "peering",
	endpoint: "endpoint",
	"direct-connect": "directConnect",
	eni: "eni",
	other: "other",
};

/**
 * How many family chips a route-table card's top border can carry on ONE line.
 * The row must never wrap: it is absolutely positioned above the card, so a
 * second line grows DOWN into the card and covers the table's own name (seen on
 * a DISH ISV table using NAT + TGW + VGW + VPCE + ENI). Past this count the tail
 * folds into a single "+N" chip whose tooltip names what was folded.
 */
const MAX_ROUTE_TABLE_GATEWAY_CHIPS = 3;

/**
 * Icon chips perched on a route-table card's top border — the same visual
 * language as the VPC's attachment badges, identifying which gateway
 * families this table's routes actually use (one chip per family, count
 * past 1, native ids in the tooltip). Replaces both the old per-target
 * text-chip row and the tier connection lines.
 *
 * Families are ordered by how many of the table's routes use them, so a table
 * that sends sixteen routes at appliance ENIs and one at a NAT leads with ENI
 * rather than with whatever the digest happened to list first.
 */
function RouteTableGatewayChips({ connectivity }: { connectivity: InsightConnectivityRef[] }) {
	if (connectivity.length === 0) return null;
	const byFamily = new Map<keyof typeof ATTACHMENT_FAMILY_META, { count: number; targets: string[] }>();
	connectivity.forEach((target) => {
		const family = CONNECTIVITY_KIND_FAMILY[target.kind] ?? "other";
		const entry = byFamily.get(family) ?? { count: 0, targets: [] };
		entry.count += 1;
		entry.targets.push(target.nativeId ?? target.label);
		byFamily.set(family, entry);
	});

	// Array.sort is stable, so equal counts keep `ATTACHMENT_FAMILY_META` order.
	const families = Array.from(byFamily.entries()).sort(([, a], [, b]) => b.count - a.count);
	const shown = families.slice(0, MAX_ROUTE_TABLE_GATEWAY_CHIPS);
	const folded = families.slice(MAX_ROUTE_TABLE_GATEWAY_CHIPS);

	return (
		<div
			data-testid="route-table-gateway-chips"
			className="absolute -top-2.5 left-2 z-10 flex max-w-[85%] flex-nowrap items-center gap-0.5"
		>
			{shown.map(([family, entry]) => (
				<AttachmentChip
					key={family}
					family={family}
					count={entry.count}
					title={`${entry.count} ${ATTACHMENT_FAMILY_META[family]?.label ?? "GW"}: ${entry.targets.join(", ")}`}
				/>
			))}
			{folded.length > 0 && (
				<span
					data-testid="route-table-gateway-chips-overflow"
					title={folded
						.map(([family, entry]) => `${entry.count} ${ATTACHMENT_FAMILY_META[family]?.label ?? "GW"}: ${entry.targets.join(", ")}`)
						.join("\n")}
					className="flex shrink-0 items-center rounded-full bg-slate-200 px-1 py-0.5 text-[8px] font-bold tracking-wide text-slate-600 shadow-sm ring-1 ring-white dark:bg-semantic-bg-tertiary dark:text-semantic-text-secondary dark:ring-semantic-bg-secondary"
				>
					+{folded.length}
				</span>
			)}
		</div>
	);
}

/**
 * Route-table card — CTX-113: rendered as a compact shelf item at the top
 * of its VPC (via `parentId`/server ordering), not connected to its
 * subnets/gateways by a derived line or badge. CTX-128 adds the table's
 * aggregated route count (one number, never one line per route) and its
 * proven connectivity targets as a bounded chip row — at most one overview
 * chip per unique route-table/target pair.
 */
export const InsightRouteTableNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const routeCount = typeof data.routeCount === "number" ? data.routeCount : undefined;
	const connectivity = Array.isArray(data.connectivity) ? (data.connectivity as InsightConnectivityRef[]) : [];

	return (
		<div
			data-testid="insight-route-table-node"
			className={`relative flex h-full w-full items-center gap-2 rounded-lg border border-emerald-200 bg-white px-2 py-1 shadow-sm dark:border-semantic-border-success-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<RouteTableGatewayChips connectivity={connectivity} />
			{routeCount != null && (
				<span
					data-testid="route-table-route-count"
					title={`${routeCount} route${routeCount === 1 ? "" : "s"}`}
					className="absolute -top-2 -right-2 z-10 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[8px] font-bold leading-none text-white dark:bg-semantic-bg-success-bold"
				>
					{routeCount}
				</span>
			)}
			<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success">
				<TableProperties className="h-4 w-4" strokeWidth={1.5} />
			</div>
			<div className="flex min-w-0 flex-col gap-0.5">
				<div className="truncate text-[10px] font-semibold leading-tight text-slate-800 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				{nativeId !== label && (
					<div className="truncate font-mono text-[8px] text-slate-400 dark:text-semantic-text-secondary">{nativeId}</div>
				)}
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightRouteTableNode.displayName = "InsightRouteTableNode";

interface GatewayKindMeta {
	badge: string;
	icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
	/** Tailwind classes for the tinted icon container + badge — one hue per gateway family so a mixed tier reads at a glance. */
	iconClass: string;
	badgeClass: string;
}

const GATEWAY_TONES = {
	sky: {
		iconClass: "bg-sky-50 text-sky-500 dark:bg-semantic-bg-tertiary dark:text-semantic-text-brand",
		badgeClass: "bg-sky-100 text-sky-700 dark:bg-semantic-bg-tertiary dark:text-semantic-text-brand",
	},
	indigo: {
		iconClass: "bg-indigo-50 text-indigo-500 dark:bg-semantic-bg-tertiary dark:text-semantic-text-brand",
		badgeClass: "bg-indigo-100 text-indigo-700 dark:bg-semantic-bg-tertiary dark:text-semantic-text-brand",
	},
	amber: {
		iconClass: "bg-amber-50 text-amber-600 dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning",
		badgeClass: "bg-amber-100 text-amber-700 dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning",
	},
	emerald: {
		iconClass: "bg-emerald-50 text-emerald-600 dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success",
		badgeClass: "bg-emerald-100 text-emerald-700 dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success",
	},
} as const;

function gatewayKindMeta(kind: string): GatewayKindMeta {
	switch (kind) {
		case "igw":
		case "internetGateway":
			return { badge: "IGW", icon: Cloud, ...GATEWAY_TONES.sky };
		case "eigw":
		case "egress-only-igw":
		case "egressOnlyInternetGateway":
			return { badge: "EIGW", icon: Cloud, ...GATEWAY_TONES.sky };
		case "nat":
		case "natGateway":
			return { badge: "NAT", icon: Network, ...GATEWAY_TONES.amber };
		case "tgw":
		case "transitGateway":
			return { badge: "TGW", icon: Route, ...GATEWAY_TONES.indigo };
		case "vgw":
		case "vpnGateway":
		case "virtualPrivateGateway":
			return { badge: "VGW", icon: Cloud, ...GATEWAY_TONES.emerald };
		case "peering":
		case "vpcPeeringConnection":
			return { badge: "PCX", icon: Waypoints, ...GATEWAY_TONES.indigo };
		case "endpoint":
		case "gateway_endpoint":
		case "s3_gateway_endpoint":
		case "vpcEndpoint":
			return { badge: "VPCE", icon: Waypoints, ...GATEWAY_TONES.sky };
		case "drg":
		case "dx":
		case "direct-connect":
		case "direct_connect":
		case "directConnectGateway":
			return { badge: "DX", icon: Route, ...GATEWAY_TONES.indigo };
		default:
			return { badge: "GW", icon: Waypoints, ...GATEWAY_TONES.sky };
	}
}

/**
 * NAT / Internet / Transit / DRG gateway card — compact horizontal pill
 * (tinted kind icon + kind badge + id) so the deduplicated gateway tier
 * reads as one uniform row regardless of which family each gateway is.
 */
export const InsightGatewayNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const kind = String(data.kind ?? "gateway");
	const meta = gatewayKindMeta(kind);
	const Icon = meta.icon;

	return (
		<div
			data-testid="insight-gateway-node"
			className={`relative flex h-full w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 shadow-sm dark:border-semantic-border-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${meta.iconClass}`}>
				<Icon className="h-4 w-4" strokeWidth={1.5} />
			</div>
			<div className="flex min-w-0 flex-col gap-0.5">
				<div className="flex items-center gap-1.5">
					<span className={`shrink-0 rounded px-1 py-px text-[8px] font-bold tracking-wide ${meta.badgeClass}`}>{meta.badge}</span>
					<span className="truncate text-[10px] font-semibold text-slate-700 dark:text-semantic-text-primary" title={label}>
						{label}
					</span>
				</div>
				{nativeId !== label && (
					<div className="truncate font-mono text-[8px] text-slate-400 dark:text-semantic-text-secondary">{nativeId}</div>
				)}
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightGatewayNode.displayName = "InsightGatewayNode";

/** Security group / NSG / NACL / security list card — rendered as its own searchable/selectable inventory item in addition to appearing as a chip on the subnet/ENI that references it. */
export const InsightSecurityGroupNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);

	return (
		<div
			data-testid="insight-security-group-node"
			className={`relative flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-rose-200 bg-white p-1.5 shadow-sm dark:border-semantic-border-danger-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<Shield className="h-7 w-7 text-rose-500" strokeWidth={1.5} />
			<div className="w-full px-1 text-center">
				<div className="truncate text-[9px] font-semibold leading-tight text-slate-800 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				<div className="mt-0.5 truncate font-mono text-[8px] text-slate-400 dark:text-semantic-text-secondary">{nativeId}</div>
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightSecurityGroupNode.displayName = "InsightSecurityGroupNode";

/**
 * CTX-128 — synthetic presentation boundary for resources the processor
 * placed outside any VPC/VCN (S3, Lambda, other account/region-scoped
 * services) so they read as an honest, dedicated regional area instead of
 * floating loose among VPC cards or being force-fit into one.
 */
export const InsightRegionalAreaNode = memo(({ data }: InsightNodeProps) => (
	<div
		className={`relative h-full w-full rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/40 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary/40 ${orphanRingClass(data)}`}
	>
		<Handle type="target" position={Position.Left} className="opacity-0" />
		<Handle type="source" position={Position.Right} className="opacity-0" />
		<div className="absolute -top-3 left-6 flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:bg-semantic-bg-secondary dark:text-semantic-text-secondary">
			<Layers className="h-3 w-3 shrink-0" />
			<span className="truncate">{data.label ?? "Regional services"}</span>
		</div>
		<OrphanBadge orphaned={data.orphaned} />
	</div>
));
InsightRegionalAreaNode.displayName = "InsightRegionalAreaNode";

/**
 * CTX-128 — honest fallback container for resources the processor could not
 * place in the VPC/regional containment hierarchy at all (never silently
 * dropped, never silently nested somewhere misleading).
 */
export const InsightOtherAreaNode = memo(({ data }: InsightNodeProps) => (
	<div
		className={`relative h-full w-full rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/30 dark:border-semantic-border-warning-primary dark:bg-semantic-bg-warning-secondary/30 ${orphanRingClass(data)}`}
	>
		<Handle type="target" position={Position.Left} className="opacity-0" />
		<Handle type="source" position={Position.Right} className="opacity-0" />
		<div className="absolute -top-3 left-6 flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:bg-semantic-bg-secondary dark:text-semantic-text-warning">
			<Box className="h-3 w-3 shrink-0" />
			<span className="truncate">{data.label ?? "Other discovered resources"}</span>
		</div>
		<OrphanBadge orphaned={data.orphaned} />
	</div>
));
InsightOtherAreaNode.displayName = "InsightOtherAreaNode";

const REGIONAL_SERVICE_ICON: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
	s3: Database,
	lambda: Zap,
};

/**
 * CTX-128 — a regional (non-VPC-contained) service card, e.g. S3 or Lambda
 * itself (as opposed to a Lambda-created ENI, which renders as a normal
 * `InsightEniNode` inside its actual subnet).
 */
export const InsightRegionalServiceNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	const kind = String(data.kind ?? "").toLowerCase();
	const Icon = REGIONAL_SERVICE_ICON[kind] ?? Box;

	return (
		<div
			data-testid="insight-regional-service-node"
			className={`relative flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm dark:border-semantic-border-primary dark:bg-semantic-bg-secondary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<Icon className="h-7 w-7 text-indigo-500" strokeWidth={1.5} />
			<div className="w-full px-1 text-center">
				<div className="truncate text-[9px] font-semibold leading-tight text-slate-800 dark:text-semantic-text-primary" title={label}>
					{label}
				</div>
				<div className="mt-0.5 truncate font-mono text-[8px] text-slate-400 dark:text-semantic-text-secondary">{nativeId}</div>
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightRegionalServiceNode.displayName = "InsightRegionalServiceNode";

/**
 * CTX-128 — synthetic "+N ENIs" summary card standing in for the ENIs a
 * busy subnet collapsed behind its bounded-disclosure cap (`eniDisclosure`).
 * Never a silent count: breaks down attached/unattached and warning/error
 * state so the summary itself is useful without expanding. Clicking toggles
 * `onToggleExpand` (deterministic expand/collapse — no pan/zoom side
 * effect); a search match hidden behind this card force-reveals its subnet
 * independently of this click (see `eniDisclosure`'s `forceVisibleIds`).
 */
export const InsightEniOverflowNode = memo(({ data }: InsightNodeProps) => {
	const total = typeof data.hiddenTotal === "number" ? data.hiddenTotal : 0;
	const attached = typeof data.hiddenAttached === "number" ? data.hiddenAttached : undefined;
	const unattached = typeof data.hiddenUnattached === "number" ? data.hiddenUnattached : undefined;
	const warning = typeof data.hiddenWarning === "number" ? data.hiddenWarning : 0;
	const error = typeof data.hiddenError === "number" ? data.hiddenError : 0;
	const expanded = Boolean(data.expanded);
	const Chevron = expanded ? ChevronUp : ChevronDown;

	return (
		<button
			type="button"
			data-testid="insight-eni-overflow-node"
			data-expanded={expanded ? "true" : "false"}
			onClick={(event) => {
				event.stopPropagation();
				data.onToggleExpand?.();
			}}
			className="flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-1.5 text-center hover:bg-slate-100 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary dark:hover:bg-semantic-state-hover"
		>
			<div className="flex items-center gap-1 text-[10px] font-bold text-slate-600 dark:text-semantic-text-primary">
				<Chevron className="h-3 w-3" />
				{expanded ? "Show fewer" : `+${total} ENIs`}
			</div>
			{(attached != null || unattached != null) && (
				<div className="text-[8px] text-slate-500 dark:text-semantic-text-secondary">
					{attached ?? 0} attached · {unattached ?? 0} unattached
				</div>
			)}
			{(warning > 0 || error > 0) && (
				<div className="text-[8px] font-semibold text-amber-600 dark:text-semantic-text-warning" data-testid="eni-overflow-warning-count">
					{error > 0 ? `${error} error${error === 1 ? "" : "s"}` : ""}
					{error > 0 && warning > 0 ? " · " : ""}
					{warning > 0 ? `${warning} warning${warning === 1 ? "" : "s"}` : ""}
				</div>
			)}
		</button>
	);
});
InsightEniOverflowNode.displayName = "InsightEniOverflowNode";

/** The VPC-level connection families and their visual identity — shared by the VPC border badges and the per-route-table gateway chips. */
const ATTACHMENT_FAMILY_META: Record<string, { label: string; icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; badgeClass: string }> = {
	igw: { label: "IGW", icon: Cloud, badgeClass: GATEWAY_TONES.sky.badgeClass },
	nat: { label: "NAT", icon: Network, badgeClass: GATEWAY_TONES.amber.badgeClass },
	tgw: { label: "TGW", icon: Route, badgeClass: GATEWAY_TONES.indigo.badgeClass },
	peering: { label: "PCX", icon: Waypoints, badgeClass: GATEWAY_TONES.indigo.badgeClass },
	directConnect: { label: "DX", icon: Route, badgeClass: GATEWAY_TONES.indigo.badgeClass },
	vgw: { label: "VGW", icon: Cloud, badgeClass: GATEWAY_TONES.emerald.badgeClass },
	endpoint: { label: "VPCE", icon: Waypoints, badgeClass: GATEWAY_TONES.sky.badgeClass },
	// An ENI next hop is not a gateway — it is an appliance in a subnet — so it
	// gets the ENI card's own icon rather than a gateway one.
	eni: { label: "ENI", icon: EthernetPort, badgeClass: GATEWAY_TONES.amber.badgeClass },
	other: { label: "GW", icon: Waypoints, badgeClass: GATEWAY_TONES.sky.badgeClass },
};

/** One icon chip (kind icon + label + count past 1) — the shared rendering for the VPC border badges and the route-table gateway chips. */
function AttachmentChip({ family, count, title }: { family: keyof typeof ATTACHMENT_FAMILY_META; count: number; title?: string }) {
	const meta = ATTACHMENT_FAMILY_META[family] ?? ATTACHMENT_FAMILY_META.igw;
	const Icon = meta.icon;
	return (
		<span
			title={title ?? `${count} ${meta.label}`}
			className={`flex items-center gap-0.5 rounded-full px-1 py-0.5 text-[8px] font-bold tracking-wide shadow-sm ring-1 ring-white dark:ring-semantic-bg-secondary ${meta.badgeClass}`}
		>
			<Icon className="h-2.5 w-2.5" strokeWidth={2} />
			{count > 1 ? `${meta.label} ${count}` : meta.label}
		</span>
	);
}

/**
 * A VPC-level connection resource drawn as its own literal node above the
 * owning VPC's card — a circular tinted icon with the family label under it
 * and a count badge once the family has more than one member (e.g. one TGW
 * node with "5" for five TGW attachments). These links terminate at the VPC
 * itself and are only later referenced by the route tables (whose own
 * associations render as border chips instead). NAT is strictly
 * subnet-resident rather than a VPC attachment, but it is a VPC-level
 * egress function, so it earns a node too. Synthesized by
 * `hierarchyDisclosure` for collapsed VPC summary cards; the expanded VPC
 * shows the real gateway cards in its tier instead.
 */
export const InsightVpcAttachmentNode = memo(({ data }: InsightNodeProps) => {
	const family = String(data.attachmentFamily ?? "igw");
	const meta = ATTACHMENT_FAMILY_META[family] ?? ATTACHMENT_FAMILY_META.igw;
	const count = typeof data.attachmentCount === "number" ? data.attachmentCount : 1;
	const gateways = Array.isArray(data.attachmentGateways) ? data.attachmentGateways : [];
	// Every member's AWS name + id in the tooltip; clicking opens the same
	// list in the sidebar with each entry navigable to the gateway's detail.
	const title =
		gateways.length > 0
			? `${count} ${meta.label}:\n${gateways
					.map((gw) => (gw.nativeId && gw.nativeId !== gw.label ? `${gw.label} (${gw.nativeId})` : (gw.nativeId ?? gw.label)))
					.join("\n")}`
			: `${count} ${meta.label}`;
	const Icon = meta.icon;
	return (
		<div
			data-testid="insight-vpc-attachment-node"
			data-attachment-family={family}
			title={title}
			className="relative flex h-full w-full flex-col items-center gap-0.5"
		>
			<div className={`relative flex h-9 w-9 items-center justify-center rounded-full shadow-sm ring-1 ring-white dark:ring-semantic-bg-secondary ${meta.badgeClass}`}>
				<Icon className="h-4 w-4" strokeWidth={1.8} />
				{count > 1 && (
					<span
						data-testid="vpc-attachment-count"
						className="absolute -right-1.5 -top-1 rounded-full bg-slate-700 px-1 text-[8px] font-bold leading-[14px] text-white dark:bg-semantic-bg-brand-bold"
					>
						{count}
					</span>
				)}
			</div>
			<span className="text-[8px] font-bold tracking-wide text-slate-500 dark:text-semantic-text-secondary">{meta.label}</span>
		</div>
	);
});
InsightVpcAttachmentNode.displayName = "InsightVpcAttachmentNode";

/** Compact counts row shared by the VPC/AZ summary cards below — subnet/ENI/route-table totals plus a warning badge, so a collapsed branch still communicates something useful without mounting any of its descendants. */
function ScopeCountsRow({ counts }: { counts?: InsightNodeData["summaryCounts"] }) {
	if (!counts) return null;
	const totalWarnings = counts.warningCount + counts.errorCount;
	return (
		<div className="flex w-full flex-col items-center gap-0.5 px-1.5">
			<div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[9px] text-slate-600 dark:text-semantic-text-secondary">
				<span data-testid="scope-summary-subnet-count">{counts.subnetCount} subnet{counts.subnetCount === 1 ? "" : "s"}</span>
				<span data-testid="scope-summary-eni-count">{counts.eniCount} ENI{counts.eniCount === 1 ? "" : "s"}</span>
				{counts.routeTableCount > 0 && <span>{counts.routeTableCount} RT{counts.routeTableCount === 1 ? "" : "s"}</span>}
			</div>
			{totalWarnings > 0 && (
				<div
					data-testid="scope-summary-warning-badge"
					className="flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700 dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning"
				>
					<AlertTriangle className="h-2.5 w-2.5" />
					{totalWarnings} warning{totalWarnings === 1 ? "" : "s"}
				</div>
			)}
		</div>
	);
}

/** Small pin-toggle affordance shown on a focused VPC/AZ card's header so a user can keep it expanded once a sibling is focused (CTX-167 "unless explicitly pinned"). */
function PinToggle({ pinned, onTogglePin }: { pinned?: boolean; onTogglePin?: () => void }) {
	if (!onTogglePin) return null;
	return (
		<button
			type="button"
			data-testid="scope-pin-toggle"
			data-pinned={pinned ? "true" : "false"}
			title={pinned ? "Unpin — this will collapse when a sibling is focused" : "Pin — keep this expanded even when a sibling is focused"}
			onClick={(event) => {
				event.stopPropagation();
				onTogglePin();
			}}
			className={`rounded-full p-0.5 ${pinned ? "text-amber-300" : "text-white/60 hover:text-white"}`}
		>
			<Pin className="h-3 w-3" fill={pinned ? "currentColor" : "none"} />
		</button>
	);
}

/**
 * CTX-167 — a collapsed VPC's compact overview card: the same established
 * green VPC header as `InsightVpcNode`, but a fixed-size leaf card (no
 * children mounted) carrying rolled-up descendant counts instead of the
 * VPC's real containment subtree. Clicking it focuses this VPC (drills in,
 * collapsing whichever sibling was focused before per the ticket's "only
 * one VPC expanded by default" rule).
 */
export const InsightVpcSummaryNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const nativeId = nativeIdOf(data, id);
	return (
		<button
			type="button"
			data-testid="insight-vpc-summary-node"
			onClick={(event) => {
				event.stopPropagation();
				data.onFocus?.();
			}}
			className={`relative flex h-full w-full flex-col items-center gap-1 rounded-2xl border-2 border-green-500 bg-green-50/30 pb-2 pt-6 text-center shadow-sm hover:bg-green-50 dark:border-semantic-border-success-primary dark:bg-semantic-bg-secondary dark:hover:bg-semantic-state-hover ${orphanRingClass(data)}`}
		>
			<div className="absolute -top-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1 shadow-md dark:bg-semantic-bg-success-bold">
				<Cloud className="h-3 w-3 text-white dark:text-semantic-text-inverse-primary" />
				<span className="text-[10px] font-bold tracking-wide text-white dark:text-semantic-text-inverse-primary">VPC</span>
			</div>
			<div className="truncate px-2 text-xs font-bold text-green-900/90 dark:text-semantic-text-primary" title={label}>
				{label}
			</div>
			<div className="truncate font-mono text-[9px] text-green-700/70 dark:text-semantic-text-secondary">{nativeId}</div>
			{data.cidr ? (
				<div className="truncate font-mono text-[9px] font-medium text-green-600/80 dark:text-semantic-text-secondary">
					{String(data.cidr)}
				</div>
			) : null}
			<ScopeCountsRow counts={data.summaryCounts} />
			<OrphanBadge orphaned={data.orphaned} />
		</button>
	);
});
InsightVpcSummaryNode.displayName = "InsightVpcSummaryNode";

/**
 * CTX-167 — a collapsed availability-group's compact overview card, the
 * leaf-card counterpart of `InsightAzNode` (same indigo dotted styling),
 * clicking focuses this AZ within the currently focused VPC.
 */
export const InsightAzSummaryNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	const counts = data.summaryCounts;
	const totalWarnings = counts ? counts.warningCount + counts.errorCount : 0;
	// Single-line chip: an AZ summary only ever says "name + N subnets + M
	// ENIs" — the previous two-story card was mostly padding ("demasiado
	// grandes para solo mostrar la cantidad de ENIs").
	return (
		<button
			type="button"
			data-testid="insight-az-summary-node"
			onClick={(event) => {
				event.stopPropagation();
				data.onFocus?.();
			}}
			className={`relative flex h-full w-full items-center justify-center gap-2 overflow-hidden rounded-lg border-2 border-dotted border-indigo-300 bg-indigo-50/40 px-3 hover:bg-indigo-50 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary dark:hover:bg-semantic-state-hover ${orphanRingClass(data)}`}
		>
			<span className="flex min-w-0 items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-indigo-500 dark:text-semantic-text-secondary">
				<MapPin className="h-2.5 w-2.5 shrink-0" />
				<span className="truncate">{label}</span>
			</span>
			{counts && (
				<span className="flex shrink-0 items-center gap-1.5 text-[9px] text-slate-600 dark:text-semantic-text-secondary">
					<span data-testid="scope-summary-subnet-count">
						{counts.subnetCount} subnet{counts.subnetCount === 1 ? "" : "s"}
					</span>
					<span data-testid="scope-summary-eni-count">
						{counts.eniCount} ENI{counts.eniCount === 1 ? "" : "s"}
					</span>
					{totalWarnings > 0 && (
						<span
							data-testid="scope-summary-warning-badge"
							className="flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700 dark:bg-semantic-bg-warning-secondary dark:text-semantic-text-warning"
						>
							<AlertTriangle className="h-2.5 w-2.5" />
							{totalWarnings}
						</span>
					)}
				</span>
			)}
			<OrphanBadge orphaned={data.orphaned} />
		</button>
	);
});
InsightAzSummaryNode.displayName = "InsightAzSummaryNode";

const SCOPE_OVERFLOW_LABEL: Record<"vpc" | "az" | "subnet" | "regional" | "shelf", string> = {
	vpc: "VPCs",
	az: "availability groups",
	subnet: "subnets",
	regional: "regional resources",
	shelf: "route tables",
};

/**
 * CTX-167 — generic "+N" overflow card standing in for whichever VPCs/AZs/
 * subnets/regional resources exceeded that scope level's visible-count cap
 * (mirrors `InsightEniOverflowNode`'s click-to-expand-all/collapse UX one
 * level up). Clicking bypasses the cap for this scope for the rest of the
 * session (`onExpandScope`) — never an unbounded "Expand all" by default.
 */
export const InsightScopeOverflowNode = memo(({ data }: InsightNodeProps) => {
	const total = typeof data.hiddenTotal === "number" ? data.hiddenTotal : 0;
	const level = (data.scopeLevel ?? "vpc") as "vpc" | "az" | "subnet" | "regional" | "shelf";
	// Only the connectivity-shelf toggle is two-way today (the shelf grid stays
	// mounted when expanded, so the same card flips to "Show fewer") — the
	// other levels keep their one-way expand, where this card simply disappears.
	const expanded = Boolean(data.expanded);
	const Chevron = expanded ? ChevronUp : ChevronDown;
	return (
		<button
			type="button"
			data-testid="insight-scope-overflow-node"
			data-scope-level={level}
			data-expanded={expanded ? "true" : "false"}
			onClick={(event) => {
				event.stopPropagation();
				data.onExpandScope?.();
			}}
			className="flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-1.5 text-center hover:bg-slate-100 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary dark:hover:bg-semantic-state-hover"
		>
			<div className="flex items-center gap-1 text-[10px] font-bold text-slate-600 dark:text-semantic-text-primary">
				<Chevron className="h-3 w-3" />
				{expanded ? `Show fewer ${SCOPE_OVERFLOW_LABEL[level]}` : `+${total} more ${SCOPE_OVERFLOW_LABEL[level]}`}
			</div>
		</button>
	);
});
InsightScopeOverflowNode.displayName = "InsightScopeOverflowNode";

/** Fallback for a digest node whose `kind`/`type` doesn't match any known semantic renderer — dashed gray card, never dropped from the graph. */
export const InsightUnresolvedNode = memo(({ data, id }: InsightNodeProps) => {
	const label = String(data.label ?? id);
	return (
		<div
			data-testid="insight-unresolved-node"
			className={`relative flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-1.5 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary ${orphanRingClass(data)}`}
		>
			<Handle type="target" position={Position.Left} className="opacity-0" />
			<Handle type="source" position={Position.Right} className="opacity-0" />
			<Box className="h-6 w-6 text-slate-300 dark:text-semantic-text-secondary" />
			<div className="truncate px-1 text-center text-[9px] font-medium text-slate-500 dark:text-semantic-text-secondary" title={label}>
				{label}
			</div>
			<OrphanBadge orphaned={data.orphaned} />
		</div>
	);
});
InsightUnresolvedNode.displayName = "InsightUnresolvedNode";

export type InsightSemanticNodeType =
	| "insightProvider"
	| "insightRegion"
	| "insightAz"
	| "insightVpc"
	| "insightSubnet"
	| "insightInstance"
	| "insightEni"
	| "insightEniOverflow"
	| "insightRouteTable"
	| "insightGateway"
	| "insightSecurityGroup"
	| "insightRegionalArea"
	| "insightOtherArea"
	| "insightRegionalService"
	| "insightUnresolved"
	| "insightVpcSummary"
	| "insightAzSummary"
	| "insightScopeOverflow"
	| "insightVpcAttachment";

/** Container types — need `h-full w-full` to fill the ELK-computed containment box instead of auto-sizing to content. */
export const INSIGHT_CONTAINER_NODE_TYPES: ReadonlySet<InsightSemanticNodeType> = new Set([
	"insightProvider",
	"insightRegion",
	"insightAz",
	"insightVpc",
	"insightSubnet",
	"insightRegionalArea",
	"insightOtherArea",
]);

const GROUP_KIND_TYPE: Record<string, InsightSemanticNodeType> = {
	providerPartition: "insightProvider",
	region: "insightRegion",
	availabilityGroup: "insightAz",
	/** CTX-128 — dedicated area for resources the processor placed outside any VPC/VCN (S3, Lambda, ...). */
	regionalServices: "insightRegionalArea",
	/** CTX-128 — honest bucket for resources the processor couldn't place anywhere else. */
	otherResources: "insightOtherArea",
	/** Canonical digest (topology schemaVersion 4.0) name for the same honest "couldn't place it" bucket. */
	unresolvedGroup: "insightOtherArea",
};

const RESOURCE_KIND_TYPE: Record<string, InsightSemanticNodeType> = {
	vpc: "insightVpc",
	vcn: "insightVpc",
	subnet: "insightSubnet",
	az: "insightAz",
	availabilitydomain: "insightAz",
	account: "insightProvider",
	region: "insightRegion",
	instance: "insightInstance",
	eni: "insightEni",
	vnic: "insightEni",
	rtb: "insightRouteTable",
	route_table: "insightRouteTable",
	igw: "insightGateway",
	"egress-only-igw": "insightGateway",
	eigw: "insightGateway",
	nat: "insightGateway",
	tgw: "insightGateway",
	drg: "insightGateway",
	vgw: "insightGateway",
	sgw: "insightGateway",
	gateway: "insightGateway",
	peering: "insightGateway",
	endpoint: "insightGateway",
	gateway_endpoint: "insightGateway",
	s3_gateway_endpoint: "insightGateway",
	"direct-connect": "insightGateway",
	direct_connect: "insightGateway",
	dx: "insightGateway",
	sg: "insightSecurityGroup",
	nsg: "insightSecurityGroup",
	security_list: "insightSecurityGroup",
	securitylist: "insightSecurityGroup",
	nacl: "insightSecurityGroup",
	network_acl: "insightSecurityGroup",
	/** CTX-128 — regional (non-VPC-contained) services; a Lambda-*created ENI* is still a plain `eni` node parented to its real subnet. */
	s3: "insightRegionalService",
	lambda: "insightRegionalService",
	/**
	 * Canonical digest resource types (topology schemaVersion 4.0 —
	 * insight-data-service `canonical.schema.ts`). The lookup below lowercases
	 * its input, so these keys are the lowercased forms of the canonical
	 * camelCase names (`networkInterface`, `routeTable`, ...). The nested
	 * `attributes`/`decorations` those nodes carry are flattened separately by
	 * `canonicalDigest.ts` — this table only resolves which semantic card
	 * renders.
	 */
	networkinterface: "insightEni",
	routetable: "insightRouteTable",
	internetgateway: "insightGateway",
	egressonlyinternetgateway: "insightGateway",
	natgateway: "insightGateway",
	transitgateway: "insightGateway",
	vpngateway: "insightGateway",
	virtualprivategateway: "insightGateway",
	vpcpeeringconnection: "insightGateway",
	vpcendpoint: "insightGateway",
	directconnectgateway: "insightGateway",
	securitygroup: "insightSecurityGroup",
	networksecuritygroup: "insightSecurityGroup",
	networkacl: "insightSecurityGroup",
};

/**
 * Digest `kind: "group"` (a synthetic ELK presentation boundary) resolves via
 * `groupKind` (`providerPartition` | `region` | `availabilityGroup`) — note
 * the caller (`InsightTopologyCanvas.toReactFlowNodes`) already passes the
 * groupKind value itself as `kind` for group nodes, so a single lookup table
 * per branch is sufficient. Otherwise falls back to the resource-type
 * allowlist; an unrecognized resource type renders as `insightUnresolved`
 * rather than being silently dropped (CTX-110 "never delete relationship/
 * resource data merely to hide it").
 */
export function insightNodeTypeFor(kind: string | undefined, digestKind?: string): InsightSemanticNodeType {
	if (digestKind === "group") return GROUP_KIND_TYPE[kind ?? ""] ?? "insightProvider";
	return RESOURCE_KIND_TYPE[(kind ?? "").toLowerCase()] ?? "insightUnresolved";
}

/**
 * The connectivity tray enclosing an expanded VPC's gateway tier: a labeled
 * band whose bottom edge sits flush on the VPC's top border (the VPC border
 * itself closes the shape — border-b-0 here), making the tier's belonging
 * read by containment instead of proximity. Non-interactive by design: the
 * gateway pills inside stay the click targets.
 */
export const InsightGatewayTrayNode = memo(({ data }: InsightNodeProps) => (
	<div
		data-testid="insight-gateway-tray"
		className="pointer-events-none relative h-full w-full rounded-t-xl border border-b-0 border-green-500/60 bg-green-50/25 dark:border-semantic-border-success-primary dark:bg-transparent"
	>
		<div className="absolute left-3 top-1 flex max-w-full items-center gap-1.5 overflow-hidden pr-3">
			<span className="text-[9px] font-bold uppercase tracking-widest text-green-700/80 dark:text-semantic-text-secondary">
				Connectivity
			</span>
			<span className="truncate font-mono text-[9px] text-green-700/60 dark:text-semantic-text-secondary">
				{String(data.label ?? "")}
			</span>
		</div>
	</div>
));
InsightGatewayTrayNode.displayName = "InsightGatewayTrayNode";

export const insightReactFlowNodeTypes = {
	insightProvider: InsightProviderNode,
	insightRegion: InsightRegionNode,
	insightAz: InsightAzNode,
	insightVpc: InsightVpcNode,
	insightSubnet: InsightSubnetNode,
	insightInstance: InsightInstanceNode,
	insightEni: InsightEniNode,
	insightEniOverflow: InsightEniOverflowNode,
	insightRouteTable: InsightRouteTableNode,
	insightGateway: InsightGatewayNode,
	insightSecurityGroup: InsightSecurityGroupNode,
	insightRegionalArea: InsightRegionalAreaNode,
	insightOtherArea: InsightOtherAreaNode,
	insightRegionalService: InsightRegionalServiceNode,
	insightUnresolved: InsightUnresolvedNode,
	insightVpcSummary: InsightVpcSummaryNode,
	insightAzSummary: InsightAzSummaryNode,
	insightScopeOverflow: InsightScopeOverflowNode,
	insightVpcAttachment: InsightVpcAttachmentNode,
	insightGatewayTray: InsightGatewayTrayNode,
};
